import { useEffect, useMemo, useState } from "react";
import { getAllTransactions } from "./TransactionService";
import { getAllCategories } from "../categories/CategoryService";
import { getAllMerchants } from "../merchants/MerchantService";
import { formatCurrency, monthKey, monthLabel, recentMonthOptions, transactionMonth, normalizeTransactionType } from "./transactionUtils";
import "./TrendDashboard.css";

export default function TrendDashboard({ refreshSignal }) {
  const options = useMemo(() => recentMonthOptions(8), []);
  const [month, setMonth] = useState(options[0]?.value || "");
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [merchants, setMerchants] = useState([]);

  useEffect(() => {
    load();
  }, [refreshSignal]);

  const load = async () => {
    const [transactionData, categoryData, merchantData] = await Promise.all([
      getAllTransactions(),
      getAllCategories(),
      getAllMerchants(),
    ]);
    setTransactions(Array.isArray(transactionData) ? transactionData : []);
    setCategories(Array.isArray(categoryData) ? categoryData : []);
    setMerchants(Array.isArray(merchantData) ? merchantData : []);
  };

  const previousMonth = useMemo(() => {
    const [year, monthValue] = month.split("-").map(Number);
    return monthKey(new Date(year, monthValue - 2, 1));
  }, [month]);

  const categoryMap = useMemo(() => Object.fromEntries(categories.map((item) => [item.id, item.categoryName])), [categories]);
  const merchantMap = useMemo(() => Object.fromEntries(merchants.map((item) => [item.id, item.merchantName])), [merchants]);

  const categoryRows = useMemo(
    () => buildTrendRows(transactions, month, previousMonth, "categoryId", categoryMap, "Uncategorized"),
    [categoryMap, month, previousMonth, transactions]
  );

  const merchantRows = useMemo(
    () => buildTrendRows(transactions, month, previousMonth, "merchantId", merchantMap, "Unassigned"),
    [merchantMap, month, previousMonth, transactions]
  );

  return (
    <div className="trend-dashboard">
      <div className="trend-dashboard-header">
        <div>
          <h2>Merchant and Category Trends</h2>
          <p>Compare the selected month against the previous month using your recorded expense transactions.</p>
        </div>
        <select className="input" value={month} onChange={(e) => setMonth(e.target.value)}>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="trend-grid">
        <TrendCard
          title={`Category trends for ${monthLabel(month)}`}
          subtitle={`Compared with ${monthLabel(previousMonth)}`}
          rows={categoryRows}
        />
        <TrendCard
          title={`Merchant trends for ${monthLabel(month)}`}
          subtitle={`Compared with ${monthLabel(previousMonth)}`}
          rows={merchantRows}
        />
      </div>
    </div>
  );
}

function TrendCard({ title, subtitle, rows }) {
  const max = Math.max(...rows.map((row) => row.current), 1);
  return (
    <section className="trend-card">
      <h3>{title}</h3>
      <p>{subtitle}</p>
      {rows.length === 0 ? (
        <div className="trend-empty">No expense data for this month.</div>
      ) : (
        <div className="trend-list">
          {rows.map((row) => (
            <div key={row.label} className="trend-row">
              <div className="trend-row-header">
                <span>{row.label}</span>
                <span>{formatCurrency(row.current)}</span>
              </div>
              <div className="trend-row-bar">
                <span style={{ width: `${(row.current / max) * 100}%` }} />
              </div>
              <div className={`trend-row-delta ${row.delta >= 0 ? "up" : "down"}`}>
                {row.delta >= 0 ? "+" : ""}
                {formatCurrency(row.delta)} vs last month
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function buildTrendRows(transactions, currentMonth, previousMonth, key, labelMap, fallbackLabel) {
  const current = {};
  const previous = {};

  transactions.forEach((transaction) => {
    if (normalizeTransactionType(transaction.transactionType) !== "EXPENSE") return;
    const bucketKey = transaction[key] || "unassigned";
    const month = transactionMonth(transaction);
    const amount = Number(transaction.transactionAmount || 0);
    if (month === currentMonth) current[bucketKey] = (current[bucketKey] || 0) + amount;
    if (month === previousMonth) previous[bucketKey] = (previous[bucketKey] || 0) + amount;
  });

  return Object.entries(current)
    .map(([bucketKey, amount]) => ({
      label: labelMap[bucketKey] || fallbackLabel,
      current: amount,
      previous: previous[bucketKey] || 0,
      delta: amount - (previous[bucketKey] || 0),
    }))
    .sort((left, right) => right.current - left.current)
    .slice(0, 8);
}
